import React, { useState, useEffect } from "react";
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Users, Zap, CheckCircle, BadgeAlert, X } from 'lucide-react';
import Footer from '../components/Footer';
import { supabase } from '../SupabaseClient';
import { Divider, message } from 'antd';

function CaseStudy() {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: "",
        businessName: "",
        businessType: "",
        email: "",
        phone: "",
        service: "",
    });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const { data, error } = await supabase
                .from('leads') // Replace with your table name
                .insert([
                    {
                        name: form.name,
                        business_name: form.businessName,
                        business_type: form.businessType,
                        email: form.email,
                        phone: form.phone,
                        service: form.service,
                        created_at: new Date().toISOString()
                    }
                ]);

            if (error) throw error;

            console.log("Form submitted successfully:", data);
            message.success("Thank you! We'll be in touch soon.");

            // Reset form
            setForm({
                name: "",
                businessName: "",
                businessType: "",
                email: "",
                phone: "",
                service: "",
            });

            setOpen(false);
        } catch (error) {
            console.error("Error submitting form:", error);
            message.error("There was an error submitting the form. Please try again.");
        }
    };
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
            <nav className="sticky top-0 z-50 px-6 py-6 backdrop-blur-sm border-b border-slate-800">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
                        <ArrowLeft className="w-5 h-5 text-blue-400" />
                        <span className="text-sm font-semibold text-slate-400">Back</span>
                    </Link>
                </div>
            </nav>

            <section className="relative py-10 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-24">
                        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                            Customer
                            <br />
                            <span className="bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
                                Success Stories
                            </span>
                        </h1>
                        <p className="text-xl text-slate-300 max-w-2xl mx-auto">
                            Real results from real companies transforming their operations with NeuroStudio
                        </p>
                    </div>

                    <div className="space-y-24">
                        {/* Tech Case Study */}
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-6">
                                    <TrendingUp className="w-4 h-4 text-blue-400" />
                                    <span className="text-sm text-blue-300 font-medium">Case Study 1</span>
                                </div>

                                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                                    Tech Industry
                                </h2>
                                <h3 className="text-2xl md:text-4xl font-bold text-white mb-6">
                                    Reduced Support Costs by 65%
                                </h3>

                                <p className="text-lg text-slate-300 leading-relaxed mb-8">
                                    A fast-scaling SaaS company serving over 10,000 users was struggling to keep up with inbound customer support. As usage increased, response times slowed, customer satisfaction declined, and support staff burnout became a growing risk.
                                    <br />
                                    <br />
                                    They needed a way to scale support immediately — without adding headcount.
                                </p>

                                <h3 className="text-2xl font-bold text-white mb-4">The Challenge</h3>
                                <p className="text-slate-300 leading-relaxed mb-6">
                                    The internal support team was operating at capacity.
                                    <br />
                                    Despite having 25 human agents, the company faced:
                                </p>

                                <ul className="space-y-3 mb-8">
                                    <li className="flex items-start space-x-3">
                                        <BadgeAlert className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">8-hour average response time</span>
                                    </li>
                                    <li className="flex items-start space-x-3">
                                        <BadgeAlert className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">65% customer satisfaction score</span>
                                    </li>
                                    <li className="flex items-start space-x-3">
                                        <BadgeAlert className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">$2.1M annually in support payroll</span>
                                    </li>
                                    <li className="flex items-start space-x-3">
                                        <BadgeAlert className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">Increasing burnout from repetitive Tier-1 requests</span>
                                    </li>
                                </ul>
                                <p className="text-slate-300 leading-relaxed mb-8">
                                    Hiring more agents would increase costs without solving the root issue.
                                </p>

                                <h3 className="text-2xl font-bold text-white mb-4">The NeuroStudio Solution</h3>
                                <p className="text-slate-300 leading-relaxed mb-8">
                                    NeuroStudio deployed an intelligent chatbot system to handle tier-1 support inquiries. The AI chatbot was trained on the client's knowledge base and could intelligently route complex issues to human agents while resolving common questions instantly.
                                </p>

                                <h3 className="text-2xl font-bold text-white mb-4">The Results</h3>
                                <p className="text-slate-300 leading-relaxed mb-4">
                                    Within the first 90 days:
                                </p>
                                <div className="grid grid-cols-2 gap-4 mb-8">
                                    <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-2xl p-6">
                                        <div className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                                            65%
                                        </div>
                                        <div className="text-sm text-slate-400">Cost Reduction</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-2xl p-6">
                                        <div className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                                            92%
                                        </div>
                                        <div className="text-sm text-slate-400">Satisfaction Score</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-2xl p-6">
                                        <div className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                                            &lt;5 min
                                        </div>
                                        <div className="text-sm text-slate-400">Response Time</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-2xl p-6">
                                        <div className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                                            3 months
                                        </div>
                                        <div className="text-sm text-slate-400">Time to Value</div>
                                    </div>
                                </div>

                                <p className="text-slate-300 leading-relaxed mb-8">
                                    The company avoided additional hires and stabilized customer experience while continuing to scale.
                                </p>

                                <h3 className="text-2xl font-bold text-white mb-4">Why This Matters</h3>
                                <p className="text-slate-300 leading-relaxed mb-6">
                                    Instead of hiring 10–15 additional agents, this company added one AI employee — backed by NeuroStudio’s automation and ongoing optimization.
                                    <br />
                                    <br />
                                    That’s the difference between tools… and outcomes.
                                </p>

                            </div>

                            <div className="bg-gradient-to-br from-blue-600/10 via-slate-900/50 to-transparent border border-slate-700/50 rounded-3xl p-8 lg:p-12 h-full flex items-center justify-center">
                                <div className="text-center">
                                    <TrendingUp className="w-24 h-24 text-blue-400 mx-auto mb-6 opacity-50" />
                                    <p className="text-slate-400 text-lg">SaaS Customer Support Platform</p>
                                </div>
                            </div>
                        </div>

                        <Divider style={{ backgroundColor: "gray" }} />

                        {/* MedSpa Case Study */}
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            <div className="bg-gradient-to-br from-emerald-600/10 via-slate-900/50 to-transparent border border-slate-700/50 rounded-3xl p-8 lg:p-12 h-full flex items-center justify-center order-2 lg:order-1">
                                <div className="text-center">
                                    <Zap className="w-24 h-24 text-emerald-400 mx-auto mb-6 opacity-50" />
                                    <p className="text-slate-400 text-lg">Medical Spa Franchise</p>
                                </div>
                            </div>

                            <div className="order-1 lg:order-2">
                                <div className="inline-flex items-center space-x-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
                                    <Zap className="w-4 h-4 text-emerald-400" />
                                    <span className="text-sm text-emerald-300 font-medium">Case Study 2</span>
                                </div>

                                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                                    MedSpa
                                </h2>
                                <h3 className="text-2xl md:text-4xl font-bold text-white mb-6">
                                    Increased Front-Desk Productivity 3x
                                </h3>

                                <p className="text-lg text-slate-300 leading-relaxed mb-8">
                                    A growing medical spa with 2 locations and a team of 20 staff members was struggling with appointment management, inbound calls, and repetitive administrative tasks. Front-desk staff spent most of their day answering the same questions instead of focusing on patient experience and revenue-generating activities.
                                </p>

                                <h3 className="text-2xl font-bold text-white mb-4">The Challenge</h3>
                                <p className="text-slate-300 leading-relaxed mb-6">
                                    The MedSpa needed to scale bookings and patient communication without hiring additional front-desk staff.
                                    <br />
                                    Key challenges included:
                                </p>

                                <ul className="space-y-3 mb-8">
                                    <li className="flex items-start space-x-3">
                                        <BadgeAlert className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">60% of front-desk time spent on repetitive inquiries</span>
                                    </li>
                                    <li className="flex items-start space-x-3">
                                        <BadgeAlert className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">Missed calls peak hours and after closing</span>
                                    </li>
                                    <li className="flex items-start space-x-3">
                                        <BadgeAlert className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">Manual intake forms and appointment confirmations</span>
                                    </li>
                                    <li className="flex items-start space-x-3">
                                        <BadgeAlert className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">High no-show rates due to inconsistent follow-ups</span>
                                    </li>
                                </ul>

                                <h3 className="text-2xl font-bold text-white mb-4">The NeuroStudio Solution</h3>
                                <p className="text-slate-300 leading-relaxed mb-8">
                                    NeuroStudio deployed an AI Front Desk Employee to handle inbound calls, SMS, and web chat.
                                    <br />
                                    The AI agent:
                                </p>
                                <ul className="space-y-3 mb-6">
                                    <li className="flex items-start space-x-3">
                                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">Answered FAQs (pricing, services, prep instructions)</span>
                                    </li>
                                    <li className="flex items-start space-x-3">
                                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">Booked and rescheduled appointments in real time</span>
                                    </li>
                                    <li className="flex items-start space-x-3">
                                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">Sent automated confirmations and reminders</span>
                                    </li>
                                    <li className="flex items-start space-x-3">
                                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">Routed high-intent callers to human staff when needed</span>
                                    </li>
                                </ul>
                                <p className="text-slate-300 leading-relaxed mb-8">
                                    The system was fully integrated with the MedSpa’s scheduling software and CRM.
                                </p>

                                <h3 className="text-2xl font-bold text-white mb-4">The Results</h3>
                                <div className="grid grid-cols-2 gap-4 mb-8">
                                    <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-2xl p-6">
                                        <div className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent mb-2">
                                            3x
                                        </div>
                                        <div className="text-sm text-slate-400">Productivity Increase for Front-Desk Staff</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-2xl p-6">
                                        <div className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent mb-2">
                                            99.2%
                                        </div>
                                        <div className="text-sm text-slate-400">Accuracy in Appointment Handling</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-2xl p-6">
                                        <div className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent mb-2">
                                            40%
                                        </div>
                                        <div className="text-sm text-slate-400">Faster Response Time</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-2xl p-6">
                                        <div className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent mb-2">
                                            $480k
                                        </div>
                                        <div className="text-sm text-slate-400">Savings vs Hiring</div>
                                    </div>
                                </div>

                                <p className="text-slate-300 leading-relaxed">
                                    Within 8 weeks, the MedSpa eliminated call backlogs, reduced no-shows, and allowed staff to focus on in-clinic patient care. Patient satisfaction increased significantly due to faster responses and 24/7 availability.                                </p>
                            </div>
                        </div>

                        <Divider style={{ backgroundColor: "gray" }} />

                        {/* Real Estate Case Study */}
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <div className="inline-flex items-center space-x-2 px-4 py-2 bg-orange-500/10 border border-orange-500/20 rounded-full mb-6">
                                    <Users className="w-4 h-4 text-orange-400" />
                                    <span className="text-sm text-orange-300 font-medium">Case Study 3</span>
                                </div>

                                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                                    Real Estate
                                </h2>
                                <h3 className="text-2xl md:text-4xl font-bold text-white mb-6">
                                    Increased Lead Conversion by 62%
                                </h3>

                                <p className="text-lg text-slate-300 leading-relaxed mb-8">
                                    A real estate brokerage with 15 agents was losing high-intent leads due to slow response times and inconsistent follow-ups. Online inquiries from Zillow, Realtor.com, and the website were often answered hours later — or not at all.
                                </p>

                                <h3 className="text-2xl font-bold text-white mb-4">The Challenge</h3>
                                <p className="text-slate-300 leading-relaxed mb-6">
                                    The brokerage needed to capture and qualify leads instantly without requiring agents to be on call 24/7.
                                    <br />
                                    Their main issues included:
                                </p>

                                <ul className="space-y-3 mb-8">
                                    <li className="flex items-start space-x-3">
                                        <BadgeAlert className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">Slow response time to inbound leads (often 1–3 hours)</span>
                                    </li>
                                    <li className="flex items-start space-x-3">
                                        <BadgeAlert className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">Missed calls after business hours</span>
                                    </li>
                                    <li className="flex items-start space-x-3">
                                        <BadgeAlert className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">Manual lead assignment and follow-ups</span>
                                    </li>
                                    <li className="flex items-start space-x-3">
                                        <BadgeAlert className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">Low conversion rate from inquiry to showing</span>
                                    </li>
                                </ul>

                                <h3 className="text-2xl font-bold text-white mb-4">The NeuroStudio Solution</h3>
                                <p className="text-slate-300 leading-relaxed mb-4">
                                    NeuroStudio implemented an AI Sales & Intake Agent that worked 24/7 across phone, SMS, and web chat.
                                    <br /> The AI agent:
                                </p>
                                <ul className="space-y-3 mb-6">
                                    <li className="flex items-start space-x-3">
                                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">Responded to new leads in under 60 seconds</span>
                                    </li>
                                    <li className="flex items-start space-x-3">
                                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">Qualified buyers and sellers automatically</span>
                                    </li>
                                    <li className="flex items-start space-x-3">
                                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">Scheduled showings and listing consultations</span>
                                    </li>
                                    <li className="flex items-start space-x-3">
                                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-1" />
                                        <span className="text-slate-300">Routed hot leads directly to the right agent</span>
                                    </li>
                                </ul>
                                <p className="text-slate-300 leading-relaxed mb-8">
                                    The system integrated with the brokerage’s CRM and calendar system
                                </p>

                                <h3 className="text-2xl font-bold text-white mb-4">The Results</h3>
                                <div className="grid grid-cols-2 gap-4 mb-8">
                                    <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-2xl p-6">
                                        <div className="text-3xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent mb-2">
                                            62%
                                        </div>
                                        <div className="text-sm text-slate-400">Increase in Lead-to-Appointment Conversion</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-2xl p-6">
                                        <div className="text-3xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent mb-2">
                                            24/7
                                        </div>
                                        <div className="text-sm text-slate-400">Coverage without additional staffing</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-2xl p-6">
                                        <div className="text-3xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent mb-2">
                                            4x
                                        </div>
                                        <div className="text-sm text-slate-400">Faster Response Time</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-2xl p-6">
                                        <div className="text-3xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent mb-2">
                                            $1.1M
                                        </div>
                                        <div className="text-sm text-slate-400">Recovered Deal Value Annually</div>
                                    </div>
                                </div>

                                <p className="text-slate-300 leading-relaxed">
                                    Within 90 days, agents spent less time chasing cold leads and more time closing deals. The brokerage increased revenue without adding headcount and improved client experience through instant, professional responses.                                </p>
                            </div>

                            <div className="bg-gradient-to-br from-orange-600/10 via-slate-900/50 to-transparent border border-slate-700/50 rounded-3xl p-8 lg:p-12 h-full flex items-center justify-center">
                                <div className="text-center">
                                    <Users className="w-24 h-24 text-orange-400 mx-auto mb-6 opacity-50" />
                                    <p className="text-slate-400 text-lg">Real Estate Brokerage Firm</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* disclaimer */}
                    <div className="mt-8 mb-2 p-4 bg-slate-800/40 border border-slate-700/30 rounded-lg">
                        <p className="text-xs text-slate-400 italic">
                            * Client details have been anonymized. Results shown are representative examples. Actual outcomes vary based on implementation and business size.                                    </p>
                    </div>

                    <div className="mt-20 bg-gradient-to-br from-blue-600/10 via-slate-900/50 to-transparent border border-slate-700/50 rounded-3xl p-12 md:p-16 text-center">
                        <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                            Ready to Write Your Success Story?
                        </h3>
                        <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
                            Join thousands of companies transforming their operations with NeuroStudio
                        </p>
                        <button className="px-10 py-5 bg-gradient-to-r from-blue-500 to-emerald-500 text-white rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 transform hover:scale-105" onClick={() => setOpen(true)}>
                            Get Started Free
                        </button>
                    </div>
                </div>
            </section>

            {/* Modal */}
            {open && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 sm:p-8 w-full max-w-lg relative shadow-2xl max-h-[90vh] overflow-y-auto">
                        <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-white hover:text-red-400">
                            <X className="w-6 h-6" />
                        </button>

                        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 text-center">Start Your AI Journey</h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <input
                                type="text"
                                name="name"
                                placeholder="Your Name"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                value={form.name}
                                onChange={handleChange}
                                required
                            />

                            <input
                                type="text"
                                name="businessName"
                                placeholder="Business Name"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                value={form.businessName}
                                onChange={handleChange}
                            />

                            <input
                                type="text"
                                name="businessType"
                                placeholder="Business Type"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                value={form.businessType}
                                onChange={handleChange}
                            />

                            <input
                                type="email"
                                name="email"
                                placeholder="Email (required)"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                value={form.email}
                                onChange={handleChange}
                                required
                            />

                            <input
                                type="tel"
                                name="phone"
                                placeholder="Phone Number (optional)"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-400"
                                value={form.phone}
                                onChange={handleChange}
                            />

                            <select
                                name="service"
                                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white"
                                value={form.service}
                                onChange={handleChange}
                                required
                            >
                                <option value="" className="text-slate-900">Select a Service</option>
                                <option value="AI Agent">AI Agent / Employee</option>
                                <option value="Chatbot">Chatbot</option>
                                <option value="Backend Automation">Backend Automation</option>
                                <option value="Web/App Design">Web / App Design</option>
                                <option value="Custom Suite">Custom Suite</option>
                            </select>

                            <button
                                type="submit"
                                className="w-full py-3 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-lg text-white font-semibold hover:opacity-90"
                            >
                                Submit
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    )
}

export default CaseStudy